import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Progress } from 'components/ui/progress';
import { ScrollArea } from 'components/ui/scroll-area';
import {
  selectPrintQueue,
  selectPrintProgress,
  selectIsProcessing,
  selectActivePrintJobId,
  selectPrintRetryCounts,
  selectPrintWarnings,
} from 'services/printing/redux/selectors';
import { PrintJob } from 'services/printing/types';
import { getJobOrderSummary } from 'services/printing/utils/getJobOrderSummary';
import { Workstation } from 'app/containers/OrderDetails/types';
import {
  FiPrinter,
  FiX,
  FiMinimize2,
  FiMaximize2,
  FiExternalLink,
  FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { usePrintQueueEvents } from 'services/printing/renderer/usePrintQueueEvents';
import {
  clearQueue,
  removeJobFromQueue,
} from 'services/printing/redux/printingSlice';

const FloatingPrintQueue: React.FC = () => {
  usePrintQueueEvents();
  const dispatch = useDispatch();
  const queue = useSelector(selectPrintQueue);
  const progress = useSelector(selectPrintProgress);
  const isProcessing = useSelector(selectIsProcessing);
  const activeJobId = useSelector(selectActivePrintJobId);
  const retryCounts = useSelector(selectPrintRetryCounts);
  const warnings = useSelector(selectPrintWarnings);
  const navigate = useNavigate();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const handleClearQueue = useCallback(() => {
    dispatch(clearQueue());
  }, [dispatch]);

  const handleCancelJob = useCallback(
    (jobId: string) => {
      dispatch(removeJobFromQueue(jobId));
    },
    [dispatch],
  );

  // Don't show if no queue
  if (!queue || queue.jobs.length === 0 || !isVisible) {
    return null;
  }

  const getJobTypeIcon = (type: PrintJob['type']) => {
    const icons = {
      picking: '📋',
      label: '🏷️',
      'carrier-page': '📄',
      assembly: '📖',
    };
    return icons[type];
  };

  const getJobTypeColor = (type: PrintJob['type']) => {
    const colors = {
      picking: 'bg-blue-500',
      label: 'bg-green-500',
      'carrier-page': 'bg-purple-500',
      assembly: 'bg-orange-500',
    };
    return colors[type];
  };

  const getWorkstationLabel = (workstation: Workstation) => {
    const labels = {
      [Workstation.Cabinet]: 'Meuble',
      [Workstation.Corner]: 'Corner',
      [Workstation.Drawer]: 'Tiroirs',
      [Workstation.Accessory]: 'Accessoires',
      [Workstation.Woodwork]: 'Menuiserie',
      [Workstation.Marketing]: 'Marketing',
      [Workstation.WoodworkSpecial]: 'Menuiserie spéciale',
    } as const;
    return labels[workstation] ?? '';
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Card className="w-64 shadow-lg border-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <FiPrinter className="h-4 w-4" />
                <span>File d'impression</span>
                <Badge
                  variant={isProcessing ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {progress.completed}/{progress.total}
                </Badge>
              </div>
              <div className="flex space-x-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsMinimized(false)}
                >
                  <FiMaximize2 className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsVisible(false)}
                >
                  <FiX className="h-3 w-3" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          {progress.total > 0 && (
            <CardContent className="pt-0">
              <Progress value={progress.percentage} className="h-2" />
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Card className="w-[28rem] shadow-lg border-2 max-h-96">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <FiPrinter className="h-4 w-4" />
              <span>File d'impression</span>
              <Badge
                variant={isProcessing ? 'default' : 'secondary'}
                className="text-xs"
              >
                {isProcessing ? 'En cours' : 'En attente'}
              </Badge>
            </div>
            <div className="flex space-x-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-destructive"
                onClick={handleClearQueue}
                title="Vider la file"
              >
                <FiTrash2 className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => navigate('/printing')}
                title="Ouvrir la page complète"
              >
                <FiExternalLink className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => setIsMinimized(true)}
              >
                <FiMinimize2 className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => setIsVisible(false)}
              >
                <FiX className="h-3 w-3" />
              </Button>
            </div>
          </CardTitle>

          {progress.total > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {progress.completed} / {progress.total} travaux
                </span>
                <span>{progress.percentage}%</span>
              </div>
              <Progress value={progress.percentage} className="h-2" />
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          <ScrollArea className="h-64">
            <div className="space-y-2">
              {queue.jobs.slice(0, 10).map((job, index) => {
                const isActive = job.id === activeJobId;
                const retryCount = retryCounts[job.id] ?? 0;
                const workstationEntries = Array.isArray(job.workstations)
                  ? job.workstations
                      .map((ws, index) => ({
                        label: getWorkstationLabel(ws),
                        key: `${job.id}-ws-${index}`,
                      }))
                      .filter(entry => entry.label.length > 0)
                  : [];
                const { orderId, customerDisplayName } =
                  getJobOrderSummary(job);
                const orderLabel = orderId ? `Order #${orderId}` : '';
                const hasHeadline = Boolean(orderLabel || customerDisplayName);
                return (
                  <div
                    key={job.id}
                    className={`flex items-center justify-between p-2 border rounded-md transition-colors ${
                      isActive
                        ? 'border-blue-500 bg-blue-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="flex items-center space-x-1">
                        <span className="text-sm">
                          {getJobTypeIcon(job.type)}
                        </span>
                        <div
                          className={`w-2 h-2 rounded-full ${getJobTypeColor(
                            job.type,
                          )}`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {orderLabel && <span>{orderLabel}</span>}
                          {customerDisplayName && (
                            <>
                              {orderLabel ? ' • ' : ''}
                              <span className="font-semibold">
                                {customerDisplayName}
                              </span>
                            </>
                          )}
                          {!hasHeadline && `#${job.partOrderId}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {job.metadata.pageCount}p • {job.metadata.format}
                        </p>
                        {(job.workstation !== undefined ||
                          workstationEntries.length > 0) && (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {job.workstation !== undefined && (
                              <Badge variant="outline" className="text-[10px]">
                                {getWorkstationLabel(job.workstation)}
                              </Badge>
                            )}
                            {workstationEntries.map(entry => (
                              <Badge
                                key={entry.key}
                                variant="outline"
                                className="text-[10px]"
                              >
                                {entry.label}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          {isActive && (
                            <Badge variant="outline" className="text-[10px]">
                              En cours
                            </Badge>
                          )}
                          {retryCount > 0 && (
                            <Badge
                              variant="destructive"
                              className="text-[10px]"
                            >
                              {retryCount} retry
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1">
                      {job.metadata.requiresFolding && (
                        <div
                          className="w-1 h-1 bg-blue-500 rounded-full"
                          title="Pliage"
                        />
                      )}
                      {job.metadata.requiresStapling && (
                        <div
                          className="w-1 h-1 bg-purple-500 rounded-full"
                          title="Agrafage"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive"
                        onClick={() => handleCancelJob(job.id)}
                        title="Retirer ce travail"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        #{index + 1}
                      </span>
                    </div>
                  </div>
                );
              })}

              {queue.jobs.length > 10 && (
                <div className="text-center p-2">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => navigate('/printing')}
                    className="text-xs"
                  >
                    +{queue.jobs.length - 10} autres travaux
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="mt-3 pt-2 border-t">
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>Total: {queue.totalPages} pages</span>
              <span>Stratégie: {queue.groupingStrategy}</span>
            </div>
          </div>

          {warnings.length > 0 && (
            <div className="mt-3 border-t pt-2">
              <p className="text-xs font-semibold text-amber-700">Warnings</p>
              <ul className="mt-1 space-y-1 text-[11px] text-amber-800 max-h-24 overflow-y-auto">
                {warnings.slice(-5).map((warning, index) => (
                  <li key={`${warning}-${index}`} className="leading-snug">
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FloatingPrintQueue;
